import React, { useState } from 'react';
import { Button, Col, Collapse, Table } from 'react-bootstrap';
import { FormCol12 } from 'motion-components';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import type { AiExtractionDetails } from './ai-extraction-decoration';

const AI_EXTRACTION_FIELD_LABELS: Record<string, string> = {
  nominalCapacity: 'Ονομαστική Χωρητικότητα',
  nominalVoltage: 'Ονομαστική Τάση',
  nominalEnergyEfficiency: 'Ονομαστική Ενεργειακή Απόδοση',
  batteryWeight: 'Βάρος μπαταρίας',
  batteryLength: 'Μήκος μπαταρίας',
  batteryWidth: 'Πλάτος μπαταρίας',
  batteryHeight: 'Ύψος μπαταρίας',
  chemistry: 'Χημική σύσταση',
  manufacturer: 'Κατασκευαστής',
  partCode: 'Κωδικός εξαρτήματος',
  cellGeometry: 'Γεωμετρία κυψέλης',
};

const STATUS_LABELS: Record<string, string> = {
  match: 'Ταίριασμα',
  minor_deviation: 'Μικρή απόκλιση',
  major_deviation: 'Σημαντική απόκλιση',
  not_found: 'Δεν βρέθηκε',
};

export type AiFileDataExtractionObject = Record<string, AiExtractionDetails>;

type AiExtractionInfoPanelProps = {
  aiFileDataExtractionObject?: AiFileDataExtractionObject | null;
  className?: string;
  onTransferValue?: (key: string, details: AiExtractionDetails) => void;
};

function getFieldLabel(key: string): string {
  return AI_EXTRACTION_FIELD_LABELS[key] ?? key;
}

function getStatusLabel(status: string | undefined): string {
  return status ? STATUS_LABELS[status] ?? status : '—';
}

export const AiExtractionInfoPanel: React.FC<AiExtractionInfoPanelProps> = ({
  aiFileDataExtractionObject,
  className = '',
  onTransferValue,
}) => {
  const [open, setOpen] = useState(false);

  if (!aiFileDataExtractionObject || typeof aiFileDataExtractionObject !== 'object') {
    return null;
  }

  const entries = Object.entries(aiFileDataExtractionObject).filter(
    (entry): entry is [string, AiExtractionDetails] =>
      entry[1] != null && typeof entry[1] === 'object',
  );

  if (entries.length === 0) return null;

  return (
    <>
      <Col xs="auto" className="d-flex align-items-center">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="ai-extraction-info-collapse"
        >
          <FaInfoCircle className="me-2" />
          Συνοπτικά Αποτελέσματα AI
          {open ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
        </Button>
      </Col>
      <FormCol12>
        <Collapse in={open}>
          <div id="ai-extraction-info-collapse" className={`mt-3 ${className}`.trim()}>
            <Table responsive bordered size="sm" className="small mb-0">
              <thead className="table-light">
                <tr>
                  <th>Πεδίο</th>
                  <th>Κατάσταση</th>
                  <th>Τιμή χρήστη</th>
                  <th>Μον. (χρήστη)</th>
                  <th>Εξαγόμενη τιμή</th>
                  <th>Μον. (εξαγ.)</th>
                  <th>Αρχείο</th>
                  <th>Τοποθεσία</th>
                  <th>Απόσπασμα</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([key, d]) => (
                  <tr key={key}>
                    <td>{getFieldLabel(key)}</td>
                    <td>{getStatusLabel(d.status)}</td>
                    <td>{d.userValue ?? '—'}</td>
                    <td>{d.userUnit ?? '—'}</td>
                    <td>
                      {d.extractedValue ?? '—'}
                      <span className="float-end">
                        {(d.status === 'minor_deviation' || d.status === 'major_deviation') &&
                          !d.isAiCorrected && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="py-0 px-1 align-baseline"
                              title="Μεταφορά της Εξαγόμενης Τιμής (AI) στην τιμή του χρήστη"
                              onClick={() => onTransferValue?.(key, d)}
                            >
                              Μεταφορά
                            </Button>
                          )}
                        {d.isAiCorrected && (
                          <span
                            className="badge rounded-pill bg-success align-middle ms-2"
                            style={{ fontSize: '100%', fontWeight: 'normal' }}
                          >
                            Μεταφέρθηκε
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{d.extractedUnit ?? '—'}</td>
                    <td>{d.extractedFile ?? '—'}</td>
                    <td>{d.extractedLocation ?? '—'}</td>
                    <td>{d.extractedSnippet ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Collapse>
      </FormCol12>
    </>
  );
};

export default AiExtractionInfoPanel;
